import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
  AppBar,
  Toolbar,
  IconButton,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AuthContext } from "../context/AuthContext";
import dayjs from "dayjs";
import { 
  MdArrowBack,
  MdAttachMoney,
  MdAdd,
} from 'react-icons/md';
import axiosInstance from './axiosInstance';

export default function Requests() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const auth = useContext(AuthContext);
  
  // User context
  const userRole = auth?.userRole || 'employee';
  const currentEmployeeId = auth?.employeeId || '';
  
  // View state
  const [view, setView] = useState('list'); // 'list', 'new-check-request'
  
  // Data state
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitLoading, setSubmitLoading] = useState(false);
  
  // Check request form state
  const [checkFormData, setCheckFormData] = useState({
    employeeId: currentEmployeeId,
    paydate: null,
    reason: "",
    reissueMethod: "",
  });
  
  const isPrivilegedUser = userRole === "admin" || userRole === "manager";
  
  // Fetch employees on mount for privileged users
  useEffect(() => {
    if (isPrivilegedUser) {
      fetchEmployees();
    }
  }, [isPrivilegedUser]);
  
  // Fetch employees (for admin/manager)
  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/employees');
      setEmployees(response.data);
    } catch (err) {
      console.error("Error fetching employees:", err);
      setError("Failed to load employees");
    } finally {
      setLoading(false);
    }
  };
  
  // Submit check request
  const submitCheckRequest = async () => {
    setError('');
    setSuccess('');
    setSubmitLoading(true);

    // Validation
    const requiredFields = isPrivilegedUser 
      ? ['employeeId', 'paydate', 'reason', 'reissueMethod']
      : ['paydate', 'reason', 'reissueMethod'];

    if (!requiredFields.every(field => checkFormData[field])) {
      setError('Please fill in all required fields');
      setSubmitLoading(false);
      return;
    }

    try {
      // Create the request data
      const requestData = {
        employeeId: isPrivilegedUser ? checkFormData.employeeId : currentEmployeeId,
        type: 'check',
        subject: `Check Request - ${checkFormData.reason}`,
        content: `Request to reissue check via ${checkFormData.reissueMethod}. Reason: ${checkFormData.reason}`,
        requestDate: dayjs(checkFormData.paydate).format('YYYY-MM-DD')
      };

      await axiosInstance.post('/employee-requests', requestData);
      
      setSuccess('Check request submitted successfully');
      setCheckFormData({
        employeeId: currentEmployeeId,
        paydate: null,
        reason: '',
        reissueMethod: ''
      });
      
      // Go back to list view
      setTimeout(() => {
        setView('list');
      }, 2000);
    } catch (err) {
      console.error("Error submitting check request:", err);
      setError(err.response?.data?.error || err.message || 'Failed to submit check request');
    } finally {
      setSubmitLoading(false);
    }
  };
  
  // Render main requests list/dashboard
  const renderRequestsList = () => (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 4 
      }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Employee Requests
        </Typography>
      </Box>
      
      {/* Request Types */}
      <Grid container spacing={3}>
        {/* Check Request */}
        <Grid item xs={12} md={6} lg={4}>
          <Paper 
            sx={{ 
              p: 3, 
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: 3
              }
            }}
            onClick={() => setView('new-check-request')}
          >
            <MdAttachMoney size={48} color={theme.palette.primary.main} />
            <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
              Check Request
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Request a check reissue for lost, stolen, or damaged paychecks
            </Typography>
          </Paper>
        </Grid>
        
        {/* Future request types can be added here */}
        <Grid item xs={12} md={6} lg={4}>
          <Paper 
            sx={{ 
              p: 3, 
              textAlign: 'center',
              opacity: 0.6,
              cursor: 'not-allowed'
            }}
          >
            <MdAdd size={48} color="#ccc" />
            <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
              More Request Types
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Additional request types coming soon
            </Typography>
          </Paper>
        </Grid>
      </Grid>
      
      {/* Info Section */}
      <Box sx={{ mt: 4 }}>
        <Alert severity="info">
          <Typography variant="body2">
            <strong>Need to communicate with your team?</strong> Use the{" "}
            <strong>Messages</strong> section in the navigation menu for direct messaging and conversations.
          </Typography>
        </Alert>
      </Box>
    </Box>
  );
  
  // Render check request form
  const renderCheckRequestForm = () => (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 2, 
        mb: 3 
      }}>
        <IconButton onClick={() => setView('list')}>
          <MdArrowBack />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          Check Request
        </Typography>
      </Box>
      
      {/* Form */}
      <Paper sx={{ p: 3, maxWidth: 600 }}>
        <Grid container spacing={3}>
          {isPrivilegedUser && (
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Employee</InputLabel>
                <Select
                  value={checkFormData.employeeId}
                  onChange={(e) => setCheckFormData(prev => ({ ...prev, employeeId: e.target.value }))}
                  label="Employee"
                >
                  {employees.map((employee) => (
                    <MenuItem key={employee.id} value={employee.id}>
                      {employee.firstName} {employee.lastName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          
          <Grid item xs={12}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Pay Date *"
                value={checkFormData.paydate}
                onChange={(newValue) => setCheckFormData(prev => ({ ...prev, paydate: newValue }))}
                renderInput={(params) => <TextField {...params} fullWidth required />}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>
          
          <Grid item xs={12}>
            <FormControl fullWidth required>
              <InputLabel>Reason for Request</InputLabel>
              <Select
                value={checkFormData.reason}
                onChange={(e) => setCheckFormData(prev => ({ ...prev, reason: e.target.value }))}
                label="Reason for Request"
              >
                <MenuItem value="lost">Lost Check</MenuItem>
                <MenuItem value="stolen">Stolen Check</MenuItem>
                <MenuItem value="damaged">Damaged Check</MenuItem>
                <MenuItem value="never_received">Never Received Check</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <FormControl fullWidth required>
              <InputLabel>Preferred Reissue Method</InputLabel>
              <Select
                value={checkFormData.reissueMethod}
                onChange={(e) => setCheckFormData(prev => ({ ...prev, reissueMethod: e.target.value }))}
                label="Preferred Reissue Method"
              >
                <MenuItem value="pick_up">Pick up at office</MenuItem>
                <MenuItem value="mail">Mail to address on file</MenuItem>
                <MenuItem value="direct_deposit">Direct deposit (if set up)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={submitCheckRequest}
                disabled={submitLoading}
                size="large"
              >
                {submitLoading ? <CircularProgress size={20} /> : 'Submit Request'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => setView('list')}
                size="large"
              >
                Cancel
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Information */}
      <Box sx={{ mt: 3 }}>
        <Alert severity="info">
          <Typography variant="body2">
            <strong>Processing Time:</strong> Check requests are typically processed within 1-2 business days. 
            You will be notified via email when your request has been approved and processed.
          </Typography>
        </Alert>
      </Box>
    </Box>
  );
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Mobile Header */}
      {isMobile && (
        <AppBar position="static" color="transparent" elevation={0}>
          <Toolbar>
            <Typography variant="h6" sx={{ flexGrow: 1, color: 'text.primary' }}>
              {view === 'list' ? 'Requests' : 'Check Request'}
            </Typography>
          </Toolbar>
        </AppBar>
      )}
      
      {/* Error/Success Messages */}
      {error && (
        <Alert severity="error" onClose={() => setError("")} sx={{ m: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess("")} sx={{ m: 2 }}>
          {success}
        </Alert>
      )}
      
      {/* Main Content */}
      {view === 'list' && renderRequestsList()}
      {view === 'new-check-request' && renderCheckRequestForm()}
    </Box>
  );
}